import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { LANG, LANG_TEMPLATE } from "@/models.ts";
import { validateEffectiveStartDateForProgramYear } from "@/utils/dates";
import { validateMinDate } from "@/utils/util";

type LanguageOption = {
  Text: string;
  Value: string;
};

type Base =
  | "InstrumentLanguagesHeard"
  | "InstrumentLanguagesUnderstood"
  | "InstrumentLanguagesSpoken";

export function buildLangAddDefaults(record: LANG): LANG {
  return {
    ...LANG_TEMPLATE,
    ReportingLEA: record.ReportingLEA,
    CSPPID: record.CSPPID,
    ProgramYear: record.ProgramYear,
    PreschoolCode: record.PreschoolCode,
    LocalChildId: record.LocalChildId,
    InstrumentOptOut: record.InstrumentOptOut,
    InstrumentDate: record.InstrumentDate,
    InstrumentLanguagesHeard1: record.InstrumentLanguagesHeard1,
    InstrumentLanguagesHeard2: record.InstrumentLanguagesHeard2,
    InstrumentLanguagesHeard3: record.InstrumentLanguagesHeard3,
    InstrumentLanguagesHeard4: record.InstrumentLanguagesHeard4,
    InstrumentLanguagesHeard5: record.InstrumentLanguagesHeard5,
    InstrumentLanguagesUnderstood1: record.InstrumentLanguagesUnderstood1,
    InstrumentLanguagesUnderstood2: record.InstrumentLanguagesUnderstood2,
    InstrumentLanguagesUnderstood3: record.InstrumentLanguagesUnderstood3,
    InstrumentLanguagesUnderstood4: record.InstrumentLanguagesUnderstood4,
    InstrumentLanguagesUnderstood5: record.InstrumentLanguagesUnderstood5,
    InstrumentLanguagesSpoken1: record.InstrumentLanguagesSpoken1,
    InstrumentLanguagesSpoken2: record.InstrumentLanguagesSpoken2,
    InstrumentLanguagesSpoken3: record.InstrumentLanguagesSpoken3,
    InstrumentLanguagesSpoken4: record.InstrumentLanguagesSpoken4,
    InstrumentLanguagesSpoken5: record.InstrumentLanguagesSpoken5,
    DLLInstrumentDesignated: record.DLLInstrumentDesignated,
    DLLTeacherDesignated: record.DLLTeacherDesignated,
    DLLTeacherDesignatedDate: record.DLLTeacherDesignatedDate,
    Id: "",
    EffectiveStartDate: "",
    EffectiveEndDate: "",
  };
}

export function useLANGFormLogic({
  form,
  lang,
  languagesOptions,
  resetKey,
  instrumentOptOut,
}: {
  form: UseFormReturn<LANG, any, LANG>;
  lang: LANG;
  languagesOptions: LanguageOption[];
  resetKey: string;
  instrumentOptOut?: string;
}) {
  const { setValue, getValues, trigger, watch } = form;

  const [heardValues, setHeardValues] = useState<string[]>([]);
  const [understoodValues, setUnderstoodValues] = useState<string[]>([]);
  const [spokenValues, setSpokenValues] = useState<string[]>([]);

  const get5 = (a?: string, b?: string, c?: string, d?: string, e?: string) =>
    [a, b, c, d, e].filter(
      (x): x is string => typeof x === "string" && x.trim().length > 0
    );

  const valuesToOptions = (values: string[]) => {
    const set = new Set(values);
    return languagesOptions.filter((x) => set.has(x.Value));
  };

  const optionsToValues = (items: Array<LanguageOption>) =>
    items
      .map((x) => x.Value)
      .filter((v): v is string => typeof v === "string" && v.length > 0);

  const writeBack5 = (base: Base, values: string[]) => {
    const trimmed = values.slice(0, 5);
    const v1 = trimmed[0] ?? "";
    const v2 = trimmed[1] ?? "";
    const v3 = trimmed[2] ?? "";
    const v4 = trimmed[3] ?? "";
    const v5 = trimmed[4] ?? "";

    setValue(`${base}1` as any, v1 as any, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue(`${base}2` as any, v2 as any, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue(`${base}3` as any, v3 as any, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue(`${base}4` as any, v4 as any, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue(`${base}5` as any, v5 as any, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  useEffect(() => {
    setHeardValues(
      get5(
        (lang as any).InstrumentLanguagesHeard1,
        (lang as any).InstrumentLanguagesHeard2,
        (lang as any).InstrumentLanguagesHeard3,
        (lang as any).InstrumentLanguagesHeard4,
        (lang as any).InstrumentLanguagesHeard5
      )
    );

    setUnderstoodValues(
      get5(
        (lang as any).InstrumentLanguagesUnderstood1,
        (lang as any).InstrumentLanguagesUnderstood2,
        (lang as any).InstrumentLanguagesUnderstood3,
        (lang as any).InstrumentLanguagesUnderstood4,
        (lang as any).InstrumentLanguagesUnderstood5
      )
    );

    setSpokenValues(
      get5(
        (lang as any).InstrumentLanguagesSpoken1,
        (lang as any).InstrumentLanguagesSpoken2,
        (lang as any).InstrumentLanguagesSpoken3,
        (lang as any).InstrumentLanguagesSpoken4,
        (lang as any).InstrumentLanguagesSpoken5
      )
    );
  }, [resetKey]);

  useEffect(() => {
    if (instrumentOptOut === "Y") {
      setHeardValues([]);
      setUnderstoodValues([]);
      setSpokenValues([]);

      writeBack5("InstrumentLanguagesHeard", []);
      writeBack5("InstrumentLanguagesUnderstood", []);
      writeBack5("InstrumentLanguagesSpoken", []);
    }
  }, [instrumentOptOut, resetKey]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "ProgramYear" && value.EffectiveStartDate) {
        trigger("EffectiveStartDate");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "InstrumentDate" && value.DLLTeacherDesignatedDate) {
        trigger("DLLTeacherDesignatedDate");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  const validateEffectiveStartDate = (
    value: string | undefined
  ): string | true => {
    const minCheck = validateMinDate(value);
    if (minCheck !== true) return minCheck;

    return validateEffectiveStartDateForProgramYear(
      value,
      getValues("ProgramYear")
    );
  };

  const validateInstrumentDate = (value: string | undefined): string | true => {
    const minCheck = validateMinDate(value);
    if (minCheck !== true) return minCheck;

    if (instrumentOptOut === "N") {
      return value ? true : "Instrument Date is required";
    }
    return true;
  };

  const validateDLLTeacherDesignatedDate = (
    value: string | undefined,
    dllTeacherDesignated: string | undefined
  ): string | true => {
    const minCheck = validateMinDate(value);
    if (minCheck !== true) return minCheck;

    if (dllTeacherDesignated === "Y" && !value) {
      return "DLL Teacher Designated Date is required when Teacher Designated is 'Y'";
    }

    if (value) {
      const instrumentDate = getValues("InstrumentDate" as any) as
        | string
        | undefined;
      if (instrumentDate && new Date(value) < new Date(instrumentDate)) {
        return "DLL Teacher Designated Date must be on or after Instrument Date";
      }
    }
    return true;
  };

  return {
    heard: {
      value: valuesToOptions(heardValues),
      onChange: (items: LanguageOption[]) => {
        const next = optionsToValues(items).slice(0, 5);
        setHeardValues(next);
        writeBack5("InstrumentLanguagesHeard", next);
      },
    },
    understood: {
      value: valuesToOptions(understoodValues),
      onChange: (items: LanguageOption[]) => {
        const next = optionsToValues(items).slice(0, 5);
        setUnderstoodValues(next);
        writeBack5("InstrumentLanguagesUnderstood", next);
      },
    },
    spoken: {
      value: valuesToOptions(spokenValues),
      onChange: (items: LanguageOption[]) => {
        const next = optionsToValues(items).slice(0, 5);
        setSpokenValues(next);
        writeBack5("InstrumentLanguagesSpoken", next);
      },
    },
    validateEffectiveStartDate,
    validateInstrumentDate,
    validateDLLTeacherDesignatedDate,
  };
}
